import { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { ArrowRight, Sparkles, Eye, EyeOff } from "lucide-react";
import { hexToHslTriplet } from "@/lib/app-utils";

const C7_SMALL_LOGO = "/c7-mark.png";
const CONNECT7_WORDMARK = "/connect7-wordmark.png";
const DEFAULT_ADMIN_BUTTON_COLOR = "#1E40AF";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(false);
  // Pre-auth branding resolved by subdomain → falls back to operator.
  const [brandLogo, setBrandLogo] = useState(C7_SMALL_LOGO);          // small C7 mark
  const [brandWordmark, setBrandWordmark] = useState(CONNECT7_WORDMARK); // full wordmark
  const [buttonColor, setButtonColor] = useState(DEFAULT_ADMIN_BUTTON_COLOR); // premium dark blue

  useEffect(() => {
    const host = (typeof window !== "undefined" && window.location.hostname) || "";
    const sub = host.split(".")[0];
    api.get(`/public/branding${sub ? `?subdomain=${encodeURIComponent(sub)}` : ""}`)
      .then((r) => {
        if (r.data?.logo_url) setBrandLogo(r.data.logo_url);
        if (r.data?.logo_wordmark_url) setBrandWordmark(r.data.logo_wordmark_url);
        if (r.data?.admin_button_color) setButtonColor(r.data.admin_button_color);
      })
      .catch(() => { /* keep default logos */ });
  }, []);

  // Login uses the same premium dark-blue primary as the Admin console.
  useEffect(() => {
    const hsl = hexToHslTriplet(buttonColor || DEFAULT_ADMIN_BUTTON_COLOR);
    const root = document.documentElement;
    if (hsl) { root.style.setProperty("--primary", hsl); root.style.setProperty("--ring", hsl); }
    return () => { root.style.removeProperty("--primary"); root.style.removeProperty("--ring"); };
  }, [buttonColor]);

  if (user) {
    // Slice 3.5g — admin-tier roles ALWAYS land on /admin, even if a
    // stale `landing_path=/workspace` is on the user record. Workspace
    // is now strictly gated to agent + supervisor roles by App.js.
    const ADMIN_ROLES = ["admin", "super_admin", "super_super_admin"];
    let lp = user.landing_path || "/workspace";
    if (ADMIN_ROLES.includes(user.role)) lp = "/admin";
    return <Navigate to={lp} replace />;
  }

  async function doLogin(force) {
    setBusy(true);
    try {
      const data = await login(email, password, force);
      toast.success("Welcome to Connect7");
      const ADMIN_ROLES = ["admin", "super_admin", "super_super_admin"];
      const dest = ADMIN_ROLES.includes(data?.user?.role || "")
        ? "/admin"
        : (data.landing_path || "/workspace");
      nav(dest);
    } catch (err) {
      // 409 = an active session already exists for this agent → offer takeover.
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 &&
          (detail?.code === "active_session" || detail === "active_session")) {
        setConflict(true);
      } else {
        toast.error(formatErr(err));
      }
    } finally {
      setBusy(false);
    }
  }

  async function submit(e) {
    e.preventDefault();
    await doLogin(false);
  }

  async function takeoverAndLogin() {
    setConflict(false);
    await doLogin(true);
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left — brand panel (hero background image) */}
      <div
        className="relative hidden lg:flex flex-col p-12 text-white overflow-hidden bg-[#0a0f2c]"
        style={{
          backgroundImage: "url('/login-hero.png')",
          backgroundSize: "cover",
          backgroundPosition: "left center",
          backgroundRepeat: "no-repeat",
        }}
        data-testid="login-hero-panel"
      >
        {/* legibility scrim — darker at top & bottom, image visible in the middle */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0f2c]/70 via-transparent to-[#0a0f2c]/85" />

        <div className="relative">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3.5 py-1.5 text-xs font-medium tracking-wide backdrop-blur-md">
            <Sparkles className="w-3.5 h-3.5 text-sky-300" />
            AI-Powered Omnichannel Contact Center
          </span>
        </div>

        <div className="relative mt-auto">
          <h1 className="font-display text-3xl xl:text-4xl font-extrabold leading-[1.1] tracking-tight drop-shadow-lg">
            Every channel. Every customer.<br />
            <span className="text-sky-300">One intelligent workspace.</span>
          </h1>
          <p className="mt-4 text-sm/relaxed max-w-md text-white/85 drop-shadow">
            Voice, messaging, email and AI copilot—unified for modern contact centers.
          </p>
        </div>
      </div>

      {/* Right — form (dark premium gradient matched to the logo + hero blues) */}
      <div className="relative flex flex-col justify-center px-6 sm:px-12 py-10 overflow-hidden bg-gradient-to-br from-[#0a0f2c] via-[#132a6b] to-[#1e40af] text-white">
        {/* premium ambience — logo blue + a faint red hint (logo transitions red→blue) */}
        <div className="absolute -top-24 -right-24 w-[460px] h-[460px] rounded-full bg-sky-400/25 blur-3xl pointer-events-none" />
        <div className="absolute bottom-[-7rem] left-[-5rem] w-[420px] h-[420px] rounded-full bg-[#2563eb]/25 blur-3xl pointer-events-none" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_82%_22%,rgba(56,189,248,0.18),transparent_55%)] pointer-events-none" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_55%_88%,rgba(220,38,38,0.12),transparent_55%)] pointer-events-none" />
        <div className="absolute inset-0 opacity-[0.6] pointer-events-none [background-image:linear-gradient(to_right,rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.05)_1px,transparent_1px)] [background-size:42px_42px] [mask-image:radial-gradient(ellipse_at_center,black,transparent_78%)]" />
        {/* seam blend into the left hero */}
        <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-black/40 to-transparent pointer-events-none" />

        <Card className="self-center w-full max-w-md p-10 mt-8 border border-slate-200 bg-white text-slate-900 backdrop-blur-sm shadow-2xl rounded-3xl">
          <div className="flex flex-col items-center mb-9">
            <img src={brandWordmark} alt="Connect7 by Haloocom" className="h-20 w-auto max-w-[320px] object-contain" data-testid="login-wordmark" />
          </div>

          <h2 className="font-display text-2xl font-bold tracking-tight text-center text-slate-900">Sign in to your workspace</h2>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email" className="text-slate-700">Work email</Label>
              <Input id="email" data-testid="login-email-input" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="bg-white text-slate-900 border-slate-300 placeholder:text-slate-400" />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pr-10 bg-white text-slate-900 border-slate-300 placeholder:text-slate-400"
                />
                <button
                  type="button"
                  data-testid="toggle-password-visibility"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  tabIndex={-1}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 hover:text-slate-700 transition"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button data-testid="login-submit-btn" type="submit" disabled={busy} className="w-full h-11 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground">
              {busy ? "Signing in…" : (<span className="flex items-center justify-center gap-2">Sign in <ArrowRight className="w-4 h-4" /></span>)}
            </Button>
          </form>
        </Card>
      </div>

      {/* Active-session conflict → confirm takeover */}
      <AlertDialog open={conflict} onOpenChange={setConflict}>
        <AlertDialogContent data-testid="active-session-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Already signed in elsewhere</AlertDialogTitle>
            <AlertDialogDescription>
              This account is currently active on another device or browser tab.
              You can only be signed in on one device at a time. Log out the
              other session and continue here?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="active-session-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="active-session-takeover" onClick={takeoverAndLogin}>
              Log out other session &amp; continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
